import { useState } from 'react';
import { z } from 'zod';

export function useFormValidation<T extends z.ZodType>(schema: T) {
    const [errors, setErrors] = useState<Record<string, string>>({});

    const validate = (data: unknown): data is z.infer<T> => {
        try {
            schema.parse(data);
            setErrors({});
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                const fieldErrors: Record<string, string> = {};
                error.issues.forEach((err) => {
                    const path = err.path.join('.');
                    fieldErrors[path] = err.message;
                });
                setErrors(fieldErrors);
            }
            return false;
        }
    };

    const validateField = (fieldName: string, value: unknown) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fieldSchema = (schema as any).shape[fieldName];
            if (fieldSchema) {
                fieldSchema.parse(value);
                setErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
            }
        } catch (error) {
            if (error instanceof z.ZodError) {
                setErrors((prev) => ({
                    ...prev,
                    [fieldName]: error.issues[0]?.message || 'Invalid value',
                }));
            }
        }
    };

    const clearError = (fieldName: string) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    };

    const clearErrors = () => setErrors({});

    return {
        errors,
        validate,
        validateField,
        clearError,
        clearErrors,
    };
}
